<script lang="ts">
  import { Volume2, VolumeX, Eye, CheckCircle, XCircle, VolumeOff } from 'lucide-svelte';
  import { scale, fade } from 'svelte/transition';
  import Button from '../ui/button.svelte';
  import type { ExerciseItem, EvaluationResult, UserAnswer, ListenTypeAnswer } from '../../types/exercise';
  import { speakFlashcard } from '../../lib/tts';
  import { _ } from 'svelte-i18n';
  import { disabledExerciseModes } from '../../stores/exerciseModes';

  interface Props {
    exercise: ExerciseItem;
    showFeedback: boolean;
    evaluationResult: EvaluationResult | null;
    onAnswerChange: (answer: UserAnswer) => void;
    onSkip?: () => void;
  }

  let { exercise, showFeedback, evaluationResult, onAnswerChange, onSkip }: Props = $props();

  // Local state
  let userInput = $state('');
  let isSpeaking = $state(false);
  let hasPlayed = $state(false);
  let showTextHint = $state(false);

  // Get mode-specific data
  let modeData = $derived(
    exercise.modeData.type === 'LISTEN_TYPE' ? exercise.modeData : null
  );

  // Update parent when input changes
  $effect(() => {
    if (userInput.trim()) {
      const answer: ListenTypeAnswer = {
        type: 'LISTEN_TYPE',
        text: userInput,
      };
      onAnswerChange(answer);
    }
  });

  // Reset state when exercise changes
  $effect(() => {
    if (exercise.id) {
      userInput = '';
      hasPlayed = false;
      showTextHint = false;
    }
  });

  // Auto-play on mount
  $effect(() => {
    if (exercise.id && !hasPlayed && modeData) {
      playAudio();
    }
  });

  // Play audio
  async function playAudio() {
    if (isSpeaking || !modeData) return;

    try {
      isSpeaking = true;
      await speakFlashcard(modeData.textToSpeak, modeData.languageCode);
      hasPlayed = true;
    } catch (error) {
      console.error('TTS error:', error);
    } finally {
      isSpeaking = false;
    }
  }

  // Disable listening exercises for 15 minutes
  function disableListeningExercises() {
    disabledExerciseModes.disableMode('LISTEN_TYPE');
    // Skip current exercise
    if (onSkip) {
      onSkip();
    }
  }
</script>

<div class="space-y-8">
  <!-- Audio player -->
  <div class="text-center" in:scale={{ duration: 300, start: 0.95 }}>
    <div class="mb-6">
      <Button
        onclick={playAudio}
        variant="outline"
        size="lg"
        class="w-32 h-32 rounded-full transition-all duration-300 shadow-xl 
               {isSpeaking 
                 ? 'bg-primary/20 scale-110 shadow-primary/30 border-primary animate-pulse' 
                 : 'bg-card hover:bg-secondary/40 border-border hover:border-primary/50'}"
        disabled={isSpeaking}
      >
        {#if isSpeaking}
          <VolumeX class="w-12 h-12 text-primary" />
        {:else}
          <Volume2 class="w-12 h-12 text-foreground" />
        {/if}
      </Button>
    </div>

    <p class="text-lg font-medium text-muted-foreground mb-4">
      {isSpeaking ? $_('exercise.playing') : $_('exercise.click_to_play')}
    </p>

    <!-- Hint and disable buttons -->
    {#if modeData && !showFeedback}
      <div class="flex items-center justify-center gap-3 mt-2">
        <Button
          onclick={() => (showTextHint = !showTextHint)}
          variant="ghost"
          size="sm"
          class="text-muted-foreground hover:text-primary"
        >
          <Eye class="w-4 h-4 mr-2" />
          {showTextHint ? $_('exercise.hide_hint') : $_('exercise.show_text')}
        </Button>

        <Button
          onclick={disableListeningExercises}
          variant="ghost"
          size="sm"
          class="text-muted-foreground hover:text-destructive"
        >
          <VolumeOff class="w-4 h-4 mr-2" />
          {$_('exercise.disable_listening_15min')}
        </Button>
      </div>

      {#if showTextHint}
        <div in:fade={{ duration: 200 }} class="mt-4 p-3 bg-secondary/30 rounded-lg inline-block backdrop-blur-sm">
            <p class="text-xl font-bold tracking-wide">
              {modeData.textToSpeak}
            </p>
        </div>
      {/if}
    {/if}
  </div>

  <!-- Input field -->
  <div>
    <label for="listen-type-input" class="block text-sm font-medium text-muted-foreground mb-3 text-center uppercase tracking-wider">
      {$_('exercise.write_what_you_hear')}
    </label>
    <input
      id="listen-type-input"
      type="text"
      bind:value={userInput}
      disabled={showFeedback}
      placeholder={$_('exercise.enter_answer')}
      class="form-input text-xl text-center w-full p-4 rounded-xl border-2 transition-all duration-200 outline-none
             {showFeedback && evaluationResult
               ? evaluationResult.isCorrect
                 ? 'border-green-500 bg-green-50 dark:bg-green-500/30 text-green-900 dark:text-green-50'
                 : 'border-red-500 bg-red-50 dark:bg-red-500/30 text-red-900 dark:text-red-50'
               : 'border-border bg-secondary/10 focus:border-primary focus:bg-background focus:ring-4 ring-primary/10'}"
    />
  </div>

  <!-- Feedback -->
  {#if showFeedback && evaluationResult}
    <div
      in:scale={{ duration: 300, start: 0.95 }}
      class="p-5 rounded-xl shadow-lg border-2 backdrop-blur-sm
        {evaluationResult.isCorrect
        ? 'bg-green-100/80 dark:bg-green-500/30 border-green-500 text-green-900 dark:text-green-50'
        : 'bg-red-100/80 dark:bg-red-500/30 border-red-500 text-red-900 dark:text-red-50'}"
    >
      <div class="flex items-start gap-3">
        {#if evaluationResult.isCorrect}
            <div class="p-1 bg-green-200 dark:bg-green-800 rounded-full">
                <CheckCircle class="w-6 h-6 text-green-700 dark:text-green-300" />
            </div>
            <div>
              <h4 class="font-bold text-lg">{$_('exercise.correct')}</h4>
              
              {#if evaluationResult.typoDetected && evaluationResult.suggestedCorrection}
                 <p class="text-sm mt-1 opacity-90">
                    {$_('exercise.typo_feedback')} "{userInput}" → {$_('exercise.correct_is')} <strong>"{evaluationResult.suggestedCorrection}"</strong>
                 </p>
              {:else}
                 <p class="opacity-90">{$_('exercise.great_job')}</p>
              {/if}
            </div>
        {:else}
            <div class="p-1 bg-red-200 dark:bg-red-800 rounded-full">
                <XCircle class="w-6 h-6 text-red-700 dark:text-red-300" />
            </div>
            <div>
              <h4 class="font-bold text-lg">{$_('exercise.incorrect')}</h4>
              
              {#if evaluationResult.typoDetected && evaluationResult.suggestedCorrection}
                 <p class="text-sm mt-1">
                   {$_('exercise.typo_feedback')} "{userInput}" → {$_('exercise.correct_is')} <strong>"{evaluationResult.suggestedCorrection}"</strong>
                 </p>
              {:else}
                 <span class="font-semibold text-lg block mt-1">
                    {$_('exercise.correct_answer_label')} {evaluationResult.correctAnswer}
                 </span>
              {/if}
            </div>
        {/if}
      </div>

      <!-- Show the text after feedback -->
      {#if modeData && !evaluationResult.isCorrect}
        <div class="mt-4 pt-4 border-t border-black/10 dark:border-white/10">
          <p class="text-sm font-medium opacity-75 uppercase tracking-wide mb-1">
            {$_('exercise.text')}
          </p>
          <p class="text-lg">
             {modeData.textToSpeak}
          </p>
        </div>
      {/if}
    </div>
  {/if}
</div>
